import ChoroplethViz from '../vizs/ChoroplethViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * Used to display thematic data, in which areas are shaded or patterned in proportion to a statistical variable that represents an aggregate summary of a geographic characteristic within each area, such as population density or per-capita income.
 *
 * Choropleth maps provide an easy way to visualize how a measurement varies across a geographic area or show the level of variability within a region.
 *
 *  ### Example Usage
 * ```javascript
 *   import {ChoroplethSeq} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ChoroplethSeq($el, data, config);
 *
 * ```
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 2 columns.
 *
 * Each row represents a region in the map.
 *
 * The first column represents the id of the region (goejson feature). See [maps](#/developer/PropDetails#features) for more info on how to get the right ids in each case.
 *
 * The second column represents value that will be used to color the region.
 *
 * | id           |  value|
 * | --------     | ------ |
 * |   ESP        |    43.4|
 * |   FRA        |    24.6|
 * |   USA        |   15.7|
 * |   GBR        |    53.5|
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * Se utiliza para mostrar datos temáticos, en los que las áreas están sombreadas o modeladas en proporción a una variable estadística que representa un resumen agregado de una característica geográfica dentro de cada área, como la densidad de población o el ingreso per cápita.
 *
 * Los mapas de coropletas proporcionan una manera fácil de visualizar cómo varía una medición en un área geográfica o mostrar el nivel de variabilidad dentro de una región.
 *
 *  ### Ejemplo de uso
 * ```javascript
 *   import {ChoroplethSeq} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ChoroplethSeq($el, data, config);
 *
 * ```
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 2 columnas.
 *
 * Cada fila representa una región en el mapa.
 *
 * La primera columna representa la identificación de la región (característica geojson). Ver [mapas](#/developer/PropDetails#features) para obtener más información sobre cómo obtener los identificadores correctos en cada caso.
 *
 * La segunda columna representa el valor que se utilizará para colorear la región.
 *
 * | id           |  valor|
 * | --------     | ------ |
 * |   ESP        |    43.4|
 * |   FRA        |    24.6|
 * |   USA        |   15.7|
 * |   GBR        |    53.5|
 *
 * {.data-table}
 * */
export default class ChoroplethSeqComponent extends BaseComponent {
  static vizClass = ChoroplethViz
  static componentLabel = 'Choropleth Map'
  static componentLabelEs = 'Mapa de coropletas'
  static dataFormat = [
    { index: 0, axis: '', label: 'id', types: [String], role: 'feature-id', roleLabel: 'Area' },
    {
      index: 1,
      axis: '',
      label: 'value',
      types: [Number, null],
      role: 'value',
      roleLabel: 'Value',
    },
  ]
  static defaultConfig = {
    title: this.componentLabel,
    valueFormat: {
      type: [String, Array, Function, null],
      default: null,
    },
    unit: {
      type: [String, Boolean, null],
      default: false,
    },
    ItemsHelper: {
      colorScale: {
        value: 'sequential2',
        options: ['sequential2', 'sequential3'],
      },
    },
    TooltipBehavior: {
      tooltipValueFormat: {
        exposed: true,
        nonEditable: false,
      },
      getHtml: function (d) {
        return `
          <span class="important">${this.formatTooltipValue(d.value)}</span>
          <br/>
          ${d.label}
        `
      },
    },
  }

  static dataRequirements = {
    requirementsStrength: 100,
  }
}
